import startCase from 'lodash/startCase';
import helperStatus from '@/helpers/status';

export const validateRequiredFields = (fields: Record<string, any>, requiredFields: string[]) => {
  const errors: Record<string, string> = {};

  requiredFields.forEach((field) => {
    if (!fields[field] || fields[field] === '') {
      errors[field] = `${startCase(field)} is required`;
    }
  });

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const validateTimeRange = (startTime: any, endTime: any) => {
  const errors: Record<string, string> = {};

  if (startTime && endTime && startTime >= endTime) {
    errors.end = 'End time must be after start time';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Status management helpers for real-time validation
export const createFormStatuses = (fields: string[]) => {
  const statuses: Record<string, helperStatus> = {};
  fields.forEach((field) => {
    statuses[field] = helperStatus.Neutral;
  });
  return statuses;
};

export const validateTimeRangeOnBlur = (startTime: any, endTime: any) => {
  if (startTime && endTime && startTime >= endTime) {
    return helperStatus.Error;
  }

  return helperStatus.Neutral;
};

export const getHelperText = (status: helperStatus, fieldName: string, customMessage?: string) => {
  if (status !== helperStatus.Error) return '';

  if (customMessage) return customMessage;

  return `${startCase(fieldName)} is required`;
};

// Status management helpers
export const resetErrorState = (formStatuses: Record<string, helperStatus>, fieldName: string) => {
  formStatuses[fieldName] = helperStatus.Neutral;
};

export const triggerErrorState = (formStatuses: Record<string, helperStatus>, fieldName: string) => {
  formStatuses[fieldName] = helperStatus.Error;
};

// Field validation on blur
export const validateFieldOnBlur = (
  formStatuses: Record<string, helperStatus>,
  fieldName: string,
  fieldValue: any,
  requiredFields: string[]
) => {
  const isRequired = requiredFields.includes(fieldName);
  const isEmpty = !fieldValue || (typeof fieldValue === 'string' && fieldValue.trim() === '');

  if (isRequired && isEmpty) {
    formStatuses[fieldName] = helperStatus.Error;
  } else {
    formStatuses[fieldName] = helperStatus.Neutral;
  }
};

// Conditional field validation (e.g., address only required for in-person)
export const validateConditionalField = (
  formStatuses: Record<string, helperStatus>,
  fieldName: string,
  fieldValue: any,
  requiredFields: string[],
  condition: boolean
) => {
  if (condition) {
    validateFieldOnBlur(formStatuses, fieldName, fieldValue, requiredFields);
  } else {
    formStatuses[fieldName] = helperStatus.Neutral;
  }
};

// Interview type validation
export const validateInterviewType = (virtual: string) => {
  const errors: Record<string, string> = {};

  if (!virtual || virtual === '') {
    errors.virtual = 'Please choose either phone or in-person';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Address validation for in-person interviews
export const validateAddressForInPerson = (isInPerson: boolean, address: any) => {
  const errors: Record<string, string> = {};

  if (isInPerson && !address) {
    errors.address = 'Please provide a meeting location';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Form validation status checker
export const hasFormErrors = (formStatuses: Record<string, helperStatus>, errors: Record<string, string>) => {
  const hasFormStatusErrors = Object.values(formStatuses).includes(helperStatus.Error);
  const hasValidationErrors = Object.keys(errors).length > 0;
  return hasFormStatusErrors || hasValidationErrors;
};

// Generic error state management factory
export const createErrorStateManager = (formStatuses: Record<string, helperStatus>) => ({
  resetErrorState: (fieldName: string) => resetErrorState(formStatuses, fieldName),
  triggerErrorState: (fieldName: string) => triggerErrorState(formStatuses, fieldName),
});

// Generic helper text generation factory
export const createHelperTextGenerator = (formStatuses: Record<string, helperStatus>) => ({
  getHelperText: (fieldName: string, customMessage?: string) => getHelperText(formStatuses[fieldName], fieldName, customMessage),
});

// Enhanced error clearing helpers
export const createErrorClearingManager = (formStatuses: Record<string, helperStatus>, errors: Record<string, string>) => ({
  clearFieldError: (fieldName: string) => {
    resetErrorState(formStatuses, fieldName);
    delete errors[fieldName];
  },
  clearAllErrors: () => {
    Object.keys(formStatuses).forEach((fieldName) => {
      resetErrorState(formStatuses, fieldName);
    });
    Object.keys(errors).forEach((fieldName) => {
      delete errors[fieldName];
    });
  },
  clearFormErrors: () => {
    Object.keys(errors).forEach((fieldName) => {
      delete errors[fieldName];
    });
  },
});

// Generic field validation with error clearing
export const createFieldValidator = (formStatuses: Record<string, helperStatus>, errors: Record<string, string>) => ({
  validateFieldOnInput: (fieldName: string) => {
    // Clear errors on input for immediate feedback
    resetErrorState(formStatuses, fieldName);
    delete errors[fieldName];
  },
  validateFieldOnBlur: (fieldName: string, fieldValue: any, requiredFields: string[]) => {
    validateFieldOnBlur(formStatuses, fieldName, fieldValue, requiredFields);
  },
});
